import { day, getOnlyDate, getWeekdaysBetween } from '~/astra'
import { components } from '~/clients/optimus-api-types.generated'
import { IdDate, usePractitionerStatusDefinitions } from '~/storm'

import { ApplyPreviewData, PreviewCellView, PreviewPractitioner } from './types'

type ApplyResponse = components['schemas']['RecurringAssignmentApplyResponse']
type DateRange = Pick<components['schemas']['RecurringAssignmentApplyRequest'], 'start_date' | 'end_date'>

export function useApplyPreviewData(data: ApplyResponse | undefined, dateRange: DateRange, practitioners: PreviewPractitioner[]) {
    const dates = getWeekdaysBetween(day(dateRange.start_date), day(dateRange.end_date)).map(date => ({ id: getOnlyDate(date), date }))
    const definitions = usePractitionerStatusDefinitions({ keyBy: 'code' })
    const assignments = new Map(data?.assignments.map(assignment => [`${assignment.practitioner_uuid}:${assignment.date}`, assignment]))
    const conflicts = new Map<string, Set<string>>()

    for (const conflict of data?.conflicts ?? []) {
        const key = `${conflict.practitioner_uuid}:${conflict.date}`
        const rooms = conflicts.get(key) ?? new Set<string>()
        rooms.add(conflict.room_code)
        conflicts.set(key, rooms)
    }

    const previewData: ApplyPreviewData = new Map()
    for (const practitioner of practitioners) {
        const cells = new Map<IdDate, PreviewCellView>()
        previewData.set(practitioner, cells)
        for (const date of dates) {
            const key = `${practitioner.uuid}:${date.id}`
            const assignment = assignments.get(key)
            cells.set(date, {
                statuses: (assignment?.status_codes ?? []).flatMap(code => {
                    const definition = definitions.get(code)
                    return definition ? [{ code, label: definition.label, color: definition.color }] : []
                }),
                locations: (assignment?.room_codes ?? []).map(roomCode => ({
                    roomCode,
                    hasConflict: conflicts.get(key)?.has(roomCode) ?? false,
                })),
                unavailable: assignment?.status_codes?.includes('UNAVAILABLE') ?? false,
            })
        }
    }
    return previewData
}
